import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

interface FeatureProps {
  title: string;
  description: string;
  icon: string;
}

const FeaturesContainer = styled.section`
  padding: 6rem 0;
  background-color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    width: 500px;
    height: 500px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(124, 58, 237, 0.08) 0%, rgba(124, 58, 237, 0) 70%);
    top: -100px;
    left: -100px;
    z-index: 0;
    animation: pulse 15s infinite alternate ease-in-out;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(236, 72, 153, 0.06) 0%, rgba(236, 72, 153, 0) 70%);
    bottom: -50px;
    right: -50px;
    z-index: 0;
    animation: pulse 12s infinite alternate-reverse ease-in-out;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1) translate(0, 0);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.05) translate(20px, 20px);
      opacity: 0.7;
    }
    100% {
      transform: scale(1) translate(0, 0);
      opacity: 0.5;
    }
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 4rem;
  padding: 0 1rem;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    width: 60px;
    height: 4px;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: ${({ theme }) => theme.colors.gradient.cta};
    border-radius: ${({ theme }) => theme.radii.full};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.gray};
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(motion.div)`
  background-color: white;
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.card};
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.grayLight};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${({ theme }) => theme.colors.gradient.cta};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const IconContainer = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.radii.lg};
  background: ${({ theme }) => theme.colors.gradient.cta};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 1.5rem;
  position: relative;
  box-shadow: ${({ theme }) => theme.shadows.glow};
  transition: ${({ theme }) => theme.transitions.default};
  
  &::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: inherit;
    padding: 4px;
    background: ${({ theme }) => theme.colors.gradient.cta};
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0.5;
    transition: opacity 0.3s ease;
  }
  
  ${FeatureCard}:hover & {
    transform: scale(1.05);
    
    &::after {
      opacity: 1;
    }
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.dark};
  letter-spacing: -0.01em;
  transition: ${({ theme }) => theme.transitions.default};
  
  ${FeatureCard}:hover & {
    background: ${({ theme }) => theme.colors.gradient.text};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.colors.gray};
  line-height: 1.6;
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.default};
  
  ${FeatureCard}:hover & {
    color: ${({ theme }) => theme.colors.grayDark};
  }
`;

const Feature: React.FC<FeatureProps> = ({ title, description, icon }) => {
  return (
    <FeatureCard
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <IconContainer>
        <span role="img" aria-label={title}>
          {icon}
        </span>
      </IconContainer>
      <FeatureTitle>{title}</FeatureTitle>
      <FeatureDescription>{description}</FeatureDescription>
    </FeatureCard>
  );
};

const FeaturesSection: React.FC = () => {
  const features: FeatureProps[] = [
    {
      title: 'Client Document Portal',
      description: 'Provide clients with a secure, user-friendly portal to upload tax documents. Automated reminders ensure timely submissions and reduce follow-ups.',
      icon: '📁',
    },
    {
      title: 'Intelligent Document Organization',
      description: 'FylePrep automatically categorizes and organizes tax documents using AI, eliminating manual sorting and reducing preparation time by up to 50%.',
      icon: '🤖',
    },
    {
      title: 'Tax Software Integration',
      description: 'Seamlessly integrate with popular tax software like UltraTax, Lacerte, and Drake. Import data directly without manual entry or switching between platforms.',
      icon: '🔄',
    },
    {
      title: 'Automated Review & Verification',
      description: 'Our platform applies smart review logic, flags potential issues, and ensures accuracy before filing. Reduce errors and avoid costly amendments.',
      icon: '✓',
    },
    {
      title: 'Team Collaboration',
      description: 'Enable your accounting team to work together on tax returns with real-time updates, comments, and task assignments to streamline workflow.',
      icon: '👥',
    },
    {
      title: 'Client Communication Hub',
      description: 'Centralize all client communications, track document requests, and maintain a complete audit trail of all interactions in one secure location.',
      icon: '💬',
    },
  ];

  return (
    <FeaturesContainer>
      <SectionHeader>
        <SectionTitle>Powerful Features for Modern Tax Professionals</SectionTitle>
        <SectionSubtitle>
          FylePrep streamlines the entire tax preparation process, helping CPAs save time, 
          reduce errors, and deliver exceptional service to their clients.
        </SectionSubtitle>
      </SectionHeader>
      
      <FeaturesGrid>
        {features.map((feature, index) => (
          <Feature
            key={index}
            title={feature.title}
            description={feature.description}
            icon={feature.icon}
          />
        ))}
      </FeaturesGrid>
    </FeaturesContainer>
  );
};

export default FeaturesSection;
